import { useState } from 'react';
import { useTranslation } from 'react-i18next';

type MemoryPeriod = 'today' | 'thisWeek' | 'thisMonth' | 'myJourney';

interface MemoryItem {
  date: string;
  treasure: string;
  treasureEn: string;
  note: string;
}

const mockMemories: Record<MemoryPeriod, MemoryItem[]> = {
  today: [
    { date: '今天', treasure: '誠信', treasureEn: 'Integrity', note: '今天在工作中選擇了坦誠，而不是隱藏。' },
  ],
  thisWeek: [
    { date: '昨天', treasure: '感恩', treasureEn: 'Gratitude', note: '早晨的陽光讓我停下來，感謝這一天。' },
    { date: '前天', treasure: '承擔', treasureEn: 'Accountability', note: '為一個過去的選擇負責，沒有逃避。' },
  ],
  thisMonth: [
    { date: '兩週前', treasure: '覺察', treasureEn: 'Awareness', note: '注意到自己在壓力下的呼吸變化。' },
    { date: '三週前', treasure: '誠信', treasureEn: 'Integrity', note: '拒絕了一個不符合中軸的邀約。' },
  ],
  myJourney: [
    { date: '三個月前', treasure: '誠信', treasureEn: 'Integrity', note: '三個月前，誠信也是你的寶藏。今天，生命溫柔地邀請你重新看見它。' },
    { date: '六個月前', treasure: '承擔', treasureEn: 'Accountability', note: '第一次意識到「承擔」不是義務，而是能力。' },
  ],
};

export default function AngelMemorySection() {
  const { t } = useTranslation();
  const [period, setPeriod] = useState<MemoryPeriod>('today');

  const periods: { key: MemoryPeriod; icon: string }[] = [
    { key: 'today', icon: 'ri-sun-line' },
    { key: 'thisWeek', icon: 'ri-calendar-line' },
    { key: 'thisMonth', icon: 'ri-moon-line' },
    { key: 'myJourney', icon: 'ri-compass-3-line' },
  ];

  const memories = mockMemories[period];

  return (
    <section className="w-full bg-background-50">
      <div className="max-w-5xl mx-auto px-4 md:px-6 py-10 md:py-14">
        {/* Header */}
        <div className="text-center mb-8">
          <h2
            className="text-lg md:text-xl text-foreground-800 mb-1"
            style={{ fontFamily: 'var(--font-heading)' }}
          >
            {t('journey.angelMemory.title')}
          </h2>
          <p className="text-xs text-foreground-400">{t('journey.angelMemory.titleZh')}</p>
          <p className="text-xs text-foreground-400 mt-1 italic">
            {t('journey.angelMemory.subtitle')}
          </p>
        </div>

        {/* Period selector */}
        <div className="flex items-center justify-center gap-2 md:gap-3 mb-6">
          {periods.map((p) => (
            <button
              key={p.key}
              onClick={() => setPeriod(p.key)}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-full text-xs border transition-all cursor-pointer whitespace-nowrap ${
                period === p.key
                  ? 'border-primary-300 bg-primary-50 text-primary-700'
                  : 'border-foreground-200 text-foreground-500 hover:border-foreground-300 hover:text-foreground-700'
              }`}
            >
              <i className={`${p.icon} text-sm`} />
              {t(`journey.angelMemory.${p.key}`)}
            </button>
          ))}
        </div>

        {/* Memory list */}
        <div className="max-w-lg mx-auto space-y-3">
          {memories.map((mem, idx) => (
            <div
              key={idx}
              className="bg-background-100 border border-foreground-200/40 rounded-xl p-4"
            >
              <div className="flex items-center gap-2 mb-2">
                <span className="text-[10px] text-foreground-400 uppercase tracking-wider">
                  {mem.date}
                </span>
                <span className="w-1 h-1 rounded-full bg-foreground-200" />
                <span className="text-xs font-medium text-primary-600">
                  {mem.treasure}
                </span>
                <span className="text-[10px] text-foreground-400">
                  {mem.treasureEn}
                </span>
              </div>
              <p className="text-sm text-foreground-700 leading-relaxed">
                {mem.note}
              </p>
            </div>
          ))}
        </div>

        {/* Example memory hint */}
        {period === 'myJourney' && (
          <p className="text-center text-[11px] text-foreground-400 mt-5 max-w-md mx-auto leading-relaxed">
            {t('journey.angelMemory.exampleMemory')}
          </p>
        )}
      </div>
    </section>
  );
}